// Lo que el backoffice hace con una agencia, sin React ni Firestore: qué usa
// de su plan, cómo se guardan las condiciones a medida, la prueba de plan y
// el margen de ajuste, los datos de la ficha y los pasos de configuración.
// Todo recibe `S` (window.AgenciasShared de la app, ver lib/agencyApp.js), así
// el catálogo de planes y agencyPlan son los mismos que en la app. Los tests
// lo cargan con el shared.js real (backoffice/test/agencia-ficha.test.mjs).
//
// Las escrituras se devuelven como { set, remove }: `set` son los campos que
// cambian y `remove` los que se borran (deleteField en Firestore).

import { ALERT_QUOTA_BY_PLAN } from '../../../cf-worker/src/lib/alertQuota.js';

export const DAY_MS = 86400000;
export const HOUR_MS = 3600000;

// Lo que se puede negociar por agencia: los mismos campos que el editor de la
// app (views/admin.js › showEditAgencyModal) más el cupo de alertas de
// directo, que el Worker ya lee (lib/alertQuota.js) y la app no enseñaba.
export function limitFields(S) {
  return [
    { key: 'maxCredits', label: S.t('agencies_plan_credits', 'Créditos incluidos') },
    { key: 'maxProfiles', label: S.t('agencies_plan_profiles', 'Perfiles LIVE') },
    { key: 'maxManagers', label: S.t('agencies_plan_managers_max', 'Managers (máx.)') },
    { key: 'maxAutoTickets', label: S.t('agencies_plan_auto_tickets_month', 'Tickets automáticos / mes') },
    { key: 'maxAlerts', label: 'Directos vigilados (alertas)', worker: true }
  ];
}

export function featureFields(S) {
  return [
    ['creatorCommunity', S.t('agencies_feature_creator_community', 'Comunidad de cada creador')],
    ['missionsAuto', S.t('agencies_feature_missions_auto', 'Misiones pre-configuradas')],
    ['missionsCustom', S.t('agencies_feature_missions_custom', 'Misiones personalizadas')],
    ['agencyEvents', S.t('agencies_feature_agency_events', 'Eventos de la agencia')],
    ['fullAnalytics', S.t('agencies_feature_full_analytics', 'Analítica de todos los creadores')],
    ['directoryListing', S.t('agencies_feature_directory', 'Aparecer en el buscador de agencias')],
    ['autoTickets', S.t('agencies_feature_auto_tickets', 'Tickets automáticos con Backstage')],
    ['manualTickets', S.t('agencies_feature_manual_tickets', 'Tickets manuales desde la consola')],
    ['prioritySupport', S.t('agencies_feature_priority_support', 'Soporte prioritario')]
  ].map(([key, label]) => ({ key, label: shortLabel(label) }));
}

// «Comunidad de cada creador (donadores…)» → «Comunidad de cada creador».
export function shortLabel(label) {
  const s = String(label || '');
  const i = s.indexOf(' (');
  return i > 0 ? s.slice(0, i) : s;
}

// Lo que el plan incluye, en el orden de «Incluido en tu plan» de la consola.
export const INCLUDED_FEATURES = [
  ['creatorStats', 'agencies_feature_creator_stats', 'Estadísticas de los creadores'],
  ['creatorCommunity', 'agencies_feature_creator_community', 'Comunidad de cada creador'],
  ['missionsAuto', 'agencies_feature_missions_auto', 'Misiones pre-configuradas'],
  ['missionsCustom', 'agencies_feature_missions_custom', 'Misiones personalizadas'],
  ['tiktokEvents', 'agencies_feature_tiktok_events', 'Asignación de eventos de TikTok'],
  ['agencyEvents', 'agencies_feature_agency_events', 'Eventos de la agencia'],
  ['news', 'agencies_feature_news', 'Comunicados a creadores'],
  ['fullAnalytics', 'agencies_feature_full_analytics', 'Analítica de todos los creadores'],
  ['directoryListing', 'agencies_feature_directory', 'Aparecer en el buscador de agencias'],
  ['directoryHighlight', 'agencies_feature_directory_highlight', 'Destacado en el buscador'],
  ['autoTickets', 'agencies_feature_auto_tickets', 'Tickets automáticos'],
  ['manualTickets', 'agencies_feature_manual_tickets', 'Tickets manuales'],
  ['prioritySupport', 'agencies_feature_priority_support', 'Soporte prioritario'],
  ['onboarding', 'agencies_feature_onboarding', 'Onboarding en las primeras 72h'],
  ['writtenSupport', 'agencies_feature_written_support', 'Soporte por escrito']
];

const toMs = (v) => {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const n = Date.parse(v);
  return Number.isFinite(n) ? n : 0;
};
export { toMs };

const int = (v) => Math.max(0, Math.floor(Number(v)));
const isSet = (v) => v !== '' && v != null && Number.isFinite(Number(v));
const clone = (v) => (v && typeof v === 'object' ? JSON.parse(JSON.stringify(v)) : v);

// ─── Uso real ───────────────────────────────────────────────────────────
// Mismo criterio que shared.loadPlanUsage (la app lo lee con firebase v8;
// aquí llegan los documentos ya cargados).
//   members     agency_members de la agencia ({ licenseType, status, inRoster })
//   profiles    número de agency_profiles
//   challenges  agency_challenges (misiones)
//   events      agency_events (los espejos `mirrorOf` no son de la agencia)
export function planUsage(S, { agency = {}, members = [], profiles = 0, challenges = [], events = [] } = {}) {
  const owner = agency.ownerId || agency.ownerUid || null;
  let staffCount = 0;
  for (const [uid, st] of Object.entries(agency.licStaff || {})) {
    if (!st || st.active === false || uid === owner) continue;
    staffCount++;
  }
  let creditsUsed = 0;
  for (const m of members) {
    if (!m || !m.licenseType || m.licenseType === 'none') continue;
    creditsUsed += (S.LICENSE_TYPES[m.licenseType] || S.LICENSE_TYPES.premium).creditCost || 0;
  }
  const activeMissions = challenges.filter((c) => c && S.missionIsOngoing(c)).length;
  const activeAgencyEvents = events.filter((e) => e && e.active === true && !e.mirrorOf).length;
  return {
    creditsUsed,
    staffCount,
    profilesCount: Number(profiles) || 0,
    activeMissions,
    activeAgencyEvents,
    ticketsAuto: agency.ticketsMode === 'auto' || agency.ticketsMode === 'hybrid'
  };
}

// Creadores vigentes: los de la consola (rosterMembers): aprobados o
// vinculados y en el roster del Backstage.
export function rosterMembers(members = []) {
  return members.filter((m) => m && (m.status === 'approved' || m.status === 'linked') && m.inRoster !== false);
}

// ─── Condiciones a medida (planOverrides), prueba y margen ──────────────
export function conditionsForm(S, agency = {}, now = Date.now()) {
  const ov = agency.planOverrides || {};
  const lim = ov.limits || {};
  const feat = ov.features || {};
  const trial = S.agencyTrial ? S.agencyTrial(agency, now) : null;
  const live = trial && trial.active ? agency.planTrial : null;
  return {
    plan: S.PLANS[agency.plan] ? agency.plan : 'silver',
    limits: Object.fromEntries(limitFields(S).map(({ key }) => [key, isSet(lim[key]) ? String(int(lim[key])) : ''])),
    features: Object.fromEntries(featureFields(S).map(({ key }) => [key, typeof feat[key] === 'boolean' ? (feat[key] ? 'yes' : 'no') : ''])),
    animPack: S.ANIM_PACKS[ov.animPack] ? ov.animPack : '',
    highlightLevel: S.HIGHLIGHT_LEVELS[ov.highlightLevel] ? ov.highlightLevel : '',
    trialArmed: false,
    trialPlan: live ? live.plan : '',
    trialDays: live ? String(Math.max(1, Number(live.days) || 7)) : '7',
    graceHours: String(Math.max(1, Math.min(720, Number(agency.planGraceHours) || S.GRACE_DEFAULT_HOURS || 24)))
  };
}

// Las condiciones nuevas encima de las que había: lo que este editor no
// enseña (otras funciones, otros límites) se conserva. El editor de la app las
// rehacía de cero y borraba, por ejemplo, el cupo de alertas.
export function mergeOverrides(S, prev, form) {
  const out = clone(prev && typeof prev === 'object' ? prev : {}) || {};
  const limits = { ...(out.limits || {}) };
  for (const { key } of limitFields(S)) {
    const v = form.limits?.[key];
    if (isSet(v)) limits[key] = int(v);
    else delete limits[key];
  }
  const features = { ...(out.features || {}) };
  for (const { key } of featureFields(S)) {
    const v = form.features?.[key];
    if (v === 'yes') features[key] = true;
    else if (v === 'no') features[key] = false;
    else delete features[key];
  }
  if (Object.keys(limits).length) out.limits = limits; else delete out.limits;
  if (Object.keys(features).length) out.features = features; else delete out.features;
  if (form.animPack && S.ANIM_PACKS[form.animPack]) out.animPack = form.animPack; else delete out.animPack;
  if (form.highlightLevel && S.HIGHLIGHT_LEVELS[form.highlightLevel]) out.highlightLevel = form.highlightLevel; else delete out.highlightLevel;
  return Object.keys(out).length ? out : null;
}

// Prueba de plan: solo a un plan por encima del contratado. Con el interruptor
// apagado se queda la que hubiera (viva); «Sin prueba» con el interruptor
// encendido la quita. Cambiar los días de la misma prueba no la reinicia.
export function nextTrial(S, agency, form, { now = Date.now(), grantedBy = '' } = {}) {
  const current = agency.planTrial && typeof agency.planTrial === 'object' ? agency.planTrial : null;
  const live = S.agencyTrial ? S.agencyTrial(agency, now) : null;
  if (!form.trialArmed) return live && live.active ? current : null;
  const pick = form.trialPlan;
  if (!pick || !S.PLANS[pick] || S.planRank(pick) <= S.planRank(form.plan)) return null;
  const days = Math.max(1, Math.min(365, Math.trunc(Number(form.trialDays) || 0) || 1));
  const startedAt = current && current.plan === pick && Number(current.startedAt) ? Number(current.startedAt) : now;
  return { plan: pick, days, startedAt, endsAt: startedAt + days * DAY_MS, grantedBy };
}

/**
 * Lo que se guarda al cambiar plan y condiciones.
 *   usage     planUsage(...) de la agencia
 * Devuelve { set, remove, issues, effective, before }. Con `issues` (la
 * agencia usa más de lo que el plan nuevo incluye), quien llama pregunta y,
 * si sigue, pasa `withGrace: true`: la agencia conserva lo que tiene durante
 * el margen (planGrace) y después se le deshabilita lo que sobre, como en la
 * app. Sin issues el margen que hubiera se quita.
 */
export function planPatch(S, agency, form, { usage, now = Date.now(), grantedBy = '', withGrace = false } = {}) {
  const plan = S.PLANS[form.plan] ? form.plan : 'silver';
  const overrides = mergeOverrides(S, agency.planOverrides, form);
  const trial = nextTrial(S, agency, { ...form, plan }, { now, grantedBy });
  const effective = S.agencyPlan({ plan, planOverrides: overrides, planTrial: trial }, now);
  const before = S.agencyPlan(agency, now);
  const issues = S.planUsageIssues(effective, usage || {});
  const hours = Math.max(1, Math.min(720, Math.trunc(Number(form.graceHours) || 0) || S.GRACE_DEFAULT_HOURS || 24));
  const set = { plan, planGraceHours: hours };
  const remove = ['maxCredits', 'maxProfiles'];
  if (overrides) set.planOverrides = overrides; else remove.push('planOverrides');
  if (trial) set.planTrial = trial; else remove.push('planTrial');
  if (issues.length && withGrace) {
    set.planGrace = {
      from: before.id, to: plan, hours,
      startedAt: now, until: now + hours * HOUR_MS,
      issues: issues.map((i) => i.key)
    };
  } else if (!issues.length) {
    remove.push('planGrace');
  }
  return { set, remove, issues, effective, before };
}

// Cupo de alertas de directo que sale del plan (sin condición a medida).
export function planAlertQuota(planId) {
  return ALERT_QUOTA_BY_PLAN[planId] ?? 0;
}

// ─── Ficha: datos y contacto ────────────────────────────────────────────
export function profileForm(agency = {}) {
  return {
    name: agency.name || '',
    description: agency.description || '',
    logoUrl: agency.logoUrl || '',
    bannerUrl: agency.bannerUrl || '',
    color: /^#[0-9a-f]{6}$/i.test(agency.color || '') ? agency.color : '#7c3aed',
    region: agency.region || '',
    contactEmail: agency.contactEmail || '',
    contactEmailHidden: agency.contactEmailHidden === true,
    contactPhone: agency.contactPhone || '',
    website: agency.website || '',
    discord: agency.discord || ''
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** { set, remove, error }: error = 'name' | 'email' | 'description' | null. */
export function profilePatch(S, agency, form) {
  const name = String(form.name || '').trim();
  const email = String(form.contactEmail || '').trim();
  const description = String(form.description || '').trim();
  const max = S.AGENCY_DESCRIPTION_MAX || 2000;
  if (!name) return { error: 'name' };
  if (!EMAIL_RE.test(email)) return { error: 'email' };
  if (description.length > max) return { error: 'description' };
  const set = {
    name,
    description,
    color: /^#[0-9a-f]{6}$/i.test(form.color || '') ? form.color : '#7c3aed',
    region: String(form.region || ''),
    contactEmail: email,
    contactEmailHidden: form.contactEmailHidden === true,
    contactPhone: String(form.contactPhone || '').trim(),
    website: String(form.website || '').trim(),
    discord: String(form.discord || '').trim(),
    logo: ''
  };
  const remove = [];
  for (const key of ['logoUrl', 'bannerUrl']) {
    const url = String(form[key] || '').trim();
    if (url) set[key] = url;
    else if (agency[key]) remove.push(key);
  }
  return { set, remove, error: null };
}

// La agencia como la tendría Firestore con los cambios del formulario: es lo
// que pinta la vista previa del editor de la ficha.
export function previewAgency(agency, form) {
  const draft = { ...agency };
  for (const [k, v] of Object.entries(profileForm(agency))) draft[k] = form[k] ?? v;
  if (!draft.logoUrl) delete draft.logoUrl;
  if (!draft.bannerUrl) delete draft.bannerUrl;
  return draft;
}

// ─── Configuración de la agencia (los pasos de «Agencia › Resumen») ─────
//   counts = { members, licensed, staff, profiles, challenges, events, library }
// Cada paso: done true / false / null (sin saberlo todavía).
export function setupSteps(S, agency = {}, counts = {}) {
  const tri = (v) => (v == null ? null : v > 0);
  const branding = [
    ['logo', agency.logoUrl, 'logo'],
    ['banner', agency.bannerUrl, 'banner'],
    ['message', agency.publicMessage, 'mensaje de presentación'],
    ['email', agency.contactEmail, 'email de contacto'],
    ['link', agency.backstageLink, 'link de reclutamiento']
  ].filter(([, ok]) => !ok).map(([, , label]) => label);
  return [
    { id: 'backstage', label: S.t('agencies_bs_title', 'Conexión BackStage'), done: tri(counts.members), hint: 'Creadores sincronizados desde el Backstage' },
    { id: 'licenses', label: S.t('agencies_mod_licenses', 'Licencias configuradas'), done: tri(counts.licensed) },
    { id: 'managers', label: S.t('agencies_ag_step_managers', 'Equipos y managers'), done: tri(counts.staff) },
    { id: 'profiles', label: S.t('agencies_plan_profiles', 'Perfiles LIVE'), done: tri(counts.profiles) },
    { id: 'missions', label: S.t('agencies_nav_missions', 'Misiones'), done: tri(counts.challenges) },
    { id: 'events', label: S.t('agencies_nav_events', 'Eventos'), done: tri(counts.events) },
    { id: 'library', label: S.t('agencies_ag_step_library', 'Biblioteca de la agencia'), done: tri(counts.library) },
    { id: 'public', label: S.t('agencies_ag_step_public', 'Perfil público'), done: branding.length === 0, hint: branding.length ? `Falta: ${branding.join(', ')}` : '' }
  ];
}

// Staff activo de la agencia (licStaff) sin el dueño: lo que cuenta para el
// tope de managers.
export function staffList(agency = {}) {
  const owner = agency.ownerId || agency.ownerUid || null;
  return Object.entries(agency.licStaff || {})
    .filter(([uid, st]) => st && uid !== owner)
    .map(([uid, st]) => ({
      uid,
      email: st.email || st.pendingEmail || '',
      name: st.customName || st.adminName || '',
      role: st.role || 'manager',
      active: st.active !== false,
      devTest: st.devTest === true
    }))
    .sort((a, b) => Number(b.active) - Number(a.active) || a.email.localeCompare(b.email));
}

// Siguiente plan del catálogo y lo que añade (para «Siguiente plan»).
export function nextPlanGains(S, effective) {
  const order = S.PLAN_ORDER || ['rookie', 'silver', 'gold', 'titanium'];
  const id = order[order.indexOf(effective.id) + 1];
  if (!id || !S.PLANS[id]) return null;
  const next = S.PLANS[id];
  const gains = INCLUDED_FEATURES
    .filter(([key]) => next.features[key] && !(effective.features || {})[key])
    .map(([, k, f]) => shortLabel(S.t(k, f)));
  return { id, label: next.label, gains };
}

// ─── Dueño ──────────────────────────────────────────────────────────────
// Dueños de la agencia: los del doc (ownerId/ownerUid) y los que tienen
// agencyRoles[id] = 'owner'. Las marcas OWNER_<id> antiguas no cuentan.
export function ownerUids(agency = {}, users = []) {
  const out = new Set();
  for (const uid of [agency.ownerId, agency.ownerUid]) if (typeof uid === 'string' && uid) out.add(uid);
  for (const u of users) {
    if (u && u.agencyRoles && u.agencyRoles[agency.id] === 'owner') out.add(u.uid || u.id);
  }
  return [...out];
}
